import React, { useMemo } from 'react';

import { useTranslation } from '@wix/yoshi-flow-editor';

import { ScrollingGallery } from '../ScrollingGallery';

import { GenericPayload, PayloadCmpProps, SupportedPayloadType } from './types';
import { getPayloadsMapped, NOT_SUPPORTED_PAYLOAD } from './getPayloadsMapped';
import { payloadsOrder, payloadTypeToComponentConfig } from './config';
import { st, classes } from './ChatMessagePayload.st.css';

export const ChatMessagePayload: React.FC<{
  additionalData: Record<string, any>[];
}> = ({ additionalData }) => {
  const { t } = useTranslation();

  const payloadSections = useMemo(() => {
    if (!additionalData.length) {
      return [];
    }
    const payloadsMapped = getPayloadsMapped(additionalData);
    const isProductsWithOneSitePage = Boolean(
      payloadsMapped.products && payloadsMapped.site_pages?.length === 1,
    );
    return [...payloadsOrder, NOT_SUPPORTED_PAYLOAD].reduce(
      (acc, payloadType) => {
        const payloads = payloadsMapped[
          payloadType as SupportedPayloadType
        ] as GenericPayload[];
        if (payloads && payloads.length) {
          const { useGallery } =
            payloadTypeToComponentConfig[payloadType as SupportedPayloadType] ??
            payloadTypeToComponentConfig.default;
          const isMultiple = payloads?.length > 1;
          const isCardPayloads = payloads.some(
            (payload) => 'url' in payload && 'img' in payload,
          );

          const payloadsViews = payloads.map((payload) => (
            <div
              className={st(
                useGallery ? classes.galleryPayload : classes.payload,
              )}
            >
              <ChatMessagePayloadItem
                key={payload.id}
                payload={payload as GenericPayload}
                props={{
                  isSmall: isMultiple && useGallery,
                  hasSpecialContainer: useGallery && isMultiple,
                  isMultiple,
                  isProductsWithOneSitePage,
                }}
              />
            </div>
          ));
          acc.push(
            <div
              className={st(
                isCardPayloads && isMultiple
                  ? classes.cardPayload
                  : classes.payload,
              )}
            >
              {useGallery && isMultiple ? (
                <ScrollingGallery
                  arialLabel={t('app.widget.ariallabel.items')}
                  arrowPosition={128}
                >
                  {payloadsViews}
                </ScrollingGallery>
              ) : isMultiple && payloadType === 'site_pages' ? (
                <div className={st(classes.linksPayload)}>{payloadsViews}</div>
              ) : (
                <>{payloadsViews}</>
              )}
            </div>,
          );
        }
        return acc;
      },
      [] as JSX.Element[],
    );
  }, [additionalData, t]);

  return <>{payloadSections}</>;
};

export interface ChatMessagePayloadItemProps {
  payload: GenericPayload;
  props?: PayloadCmpProps;
}

export const ChatMessagePayloadItem: React.FC<ChatMessagePayloadItemProps> = ({
  payload,
  props,
}) =>
  (
    payloadTypeToComponentConfig[payload.type as SupportedPayloadType] ??
    payloadTypeToComponentConfig.default
  ).builder(payload as GenericPayload as any, props ?? {});
